import { queryOptions, useQuery } from '@tanstack/react-query';
import { type ListSupportTicketsResponse, listSupportTicketsContract } from '@bliks-io/contracts';
import { apiClient } from '../client';
import { supportTicketKeys } from '../query-keys';

const supportTicketsQueryOptions = () =>
  queryOptions({
    queryKey: supportTicketKeys.lists(),
    // Zammad's search index lags a few seconds behind ticket creation; keep
    // the cache (incl. the optimistic entry from useCreateSupportTicket)
    // authoritative long enough that a remount refetch cannot serve a stale
    // index state that would drop the just-created ticket.
    staleTime: 30_000,
    refetchIntervalInBackground: false,
    queryFn: async ({ signal }): Promise<ListSupportTicketsResponse> => {
      const { data } = await apiClient.get<ListSupportTicketsResponse>(
        listSupportTicketsContract.path,
        { signal }
      );
      return data;
    }
  });

export function useSupportTickets() {
  return useQuery({
    ...supportTicketsQueryOptions(),
    // Status changes made in Zammad; slower than the open ticket, and far past
    // the index lag above, so a poll never drops a just-created ticket.
    refetchInterval: 60_000
  });
}

const countNewReplies = (response: ListSupportTicketsResponse) =>
  response.tickets.filter((ticket) => ticket.hasNewReply).length;

/**
 * Tickets with a reply the customer has not opened yet, for the sidebar badge.
 * As slow as the task badge: it shows on every page, and focus, mount and
 * opening a ticket already refresh it on every visible event.
 */
export function useSupportTicketsWithNewReply() {
  return useQuery({
    ...supportTicketsQueryOptions(),
    refetchInterval: 120_000,
    select: countNewReplies
  });
}
